import { useState, useEffect, useRef } from 'react';
import {
    Plus,
    X,
    Type,
    ChevronDown,
    AlignLeft,
    Camera,
    Video,
    Calendar,
    SquareCheckBig,
    GripVertical,
    PenLine,
    Lock,
    ChevronDown as ChevronDownIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import PermissionsSelector from '../PermissionsSelector';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

const COLORS = [
    '#F7DC6F', // yellow
    '#3B82F6', // blue
    '#F59E0B', // amber
    '#EF4444', // red
    '#EC4899', // pink
    '#8B5CF6', // violet
    '#10B981', // emerald
    '#06B6D4', // cyan
    '#84CC16', // lime
    '#F97316', // orange
    '#DC2626', // red-600
    '#059669', // emerald-600
    '#14B8A6', // teal
    '#6366F1', // indigo
    '#1E3A5F', // navy
];

const SECTION_EMOJIS = [
    '📋',
    '📝',
    '✏️',
    '📌',
    '📍',
    '🏢',
    '🏗️',
    '🔨',
    '🔧',
    '⚙️',
    '🛠️',
    '📐',
    '📏',
    '📊',
    '📈',
    '📉',
    '💼',
    '👤',
    '👥',
    '📞',
    '📧',
    '🌍',
    '🗺️',
    '🚀',
    '✅',
    '✓',
    '❌',
    '⚠️',
    '🚨',
    '⭐',
    '🔔',
    '📢',
    '📷',
    '📸',
    '🎬',
    '🎥',
    '📹',
    '🖼️',
    '💾',
    '📁',
    '🔒',
    '🔓',
    '🔑',
    '🏷️',
    '💡',
    '🔍',
    '🔎',
    '📱',
    '💻',
    '⌨️',
    '🖱️',
    '🖨️',
    '📄',
    '📃',
    '📑',
    '📜',
    '📦',
    '📮',
    '📪',
    '📫',
    '✉️',
    '💌',
    '🎯',
    '🎲',
    '🏠',
    '🪧',
    '😊',
];

function getFieldTypes() {
    return [
        { id: 'text', label: i18n.t('procedures:champTexte'), icon: Type },
        { id: 'checkbox', label: i18n.t('procedures:casesACocher'), icon: SquareCheckBig },
        { id: 'dropdown', label: i18n.t('procedures:listeDeroulante'), icon: ChevronDown },
        { id: 'comment', label: i18n.t('procedures:champDeCommentaire'), icon: AlignLeft },
        { id: 'photo', label: i18n.t('procedures:photoCertifiee'), icon: Camera },
        { id: 'video', label: i18n.t('procedures:videoCertifiee'), icon: Video },
        { id: 'date', label: i18n.t('procedures:date'), icon: Calendar },
        { id: 'signature', label: i18n.t('procedures:signature'), icon: PenLine },
    ];
}

const getFieldIcon = (type) => {
    const fieldType = getFieldTypes().find((ft) => ft.id === type);
    return fieldType?.icon || Type;
};

const createDefaultSection = () => ({
    id: Date.now(),
    name: '',
    description: '',
    emoji: '📋',
    fields: [],
    isDefault: true,
});

const initialFormState = {
    name: '',
    description: '',
    color: COLORS[0],
    sections: [createDefaultSection()],
    is_locked: false,
    // [DISABLED] include_photo_certificates: false,
    selected_companies: [],
};

// Composant pour un champ sortable
function SortableField({ field, sectionIndex, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: `field-${sectionIndex}-${field.id}`,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const dragHandle = (
        <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing py-1 text-muted-foreground hover:text-foreground flex-shrink-0"
        >
            <GripVertical className="h-4 w-4" />
        </div>
    );

    return (
        <div ref={setNodeRef} style={style}>
            {typeof children === 'function' ? children({ dragHandle }) : children}
        </div>
    );
}

// Composant pour une section sortable
function SortableSection({ section, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: `section-${section.id}`,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const dragHandle = (
        <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing py-2 text-muted-foreground hover:text-foreground flex-shrink-0 self-start"
        >
            <GripVertical className="h-5 w-5" />
        </div>
    );

    return (
        <div ref={setNodeRef} style={style}>
            {typeof children === 'function' ? children({ dragHandle }) : children}
        </div>
    );
}

export const FORM_PROCEDURE_CREATE_ID = 'form-procedure-create';

export default function FormProcedureCreate({ onSubmit, onReset, onFormDataChange, initialData }) {
    const { t } = useTranslation();
    const FIELD_TYPES = getFieldTypes();
    const [formData, setFormData] = useState(() => {
        if (initialData) {
            return initialData;
        }
        return initialFormState;
    });
    const [expandedEmojiPicker, setExpandedEmojiPicker] = useState(null);

    // DnD sensors configuration
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Reset form when onReset changes (panel closes)
    useEffect(() => {
        if (onReset) {
            setFormData({
                ...initialFormState,
                sections: [createDefaultSection()],
            });
            setExpandedEmojiPicker(null);
        }
    }, [onReset]);

    // Notify parent of form data changes (sans inclure onFormDataChange dans les deps)
    const onFormDataChangeRef = useRef(onFormDataChange);
    onFormDataChangeRef.current = onFormDataChange;

    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current(formData);
        }
    }, [formData]);

    const handleChange = (field, value) => {
        // Éviter de définir une couleur vide (problème avec Radix Select)
        if (field === 'color' && !value) {
            return;
        }
        setFormData((prev) => ({ ...prev, [field]: value }));
        console.log('Form data updated:', { ...formData, [field]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            return;
        }
        onSubmit(formData);
    };

    // Sections management
    const addSection = () => {
        setFormData((prev) => ({
            ...prev,
            sections: [
                ...prev.sections,
                {
                    id: Date.now(),
                    name: '',
                    description: '',
                    emoji: '📋',
                    fields: [],
                },
            ],
        }));
    };

    const removeSection = (sectionIndex) => {
        if (formData.sections.length <= 1) {
            return;
        }
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.filter((_, index) => index !== sectionIndex),
        }));
    };

    const updateSection = (sectionIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, index) =>
                index === sectionIndex ? { ...section, [field]: value } : section,
            ),
        }));
    };

    // Fields management
    const addField = (sectionIndex, fieldType) => {
        const newField = {
            id: Date.now(),
            type: fieldType,
            label: '',
            required: false,
        };

        if (fieldType === 'dropdown' || fieldType === 'checkbox') {
            newField.options = [''];
        }
        if (fieldType === 'photo') {
            newField.instructions = '';
            newField.minPhotos = 1;
        }
        if (fieldType === 'video') {
            newField.instructions = '';
            newField.minVideos = 1;
        }

        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, index) =>
                index === sectionIndex
                    ? {
                          ...section,
                          fields: [...section.fields, newField],
                      }
                    : section,
            ),
        }));
    };

    const addOption = (sectionIndex, fieldIndex) => {
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, sIndex) =>
                sIndex === sectionIndex
                    ? {
                          ...section,
                          fields: section.fields.map((field, fIndex) =>
                              fIndex === fieldIndex ? { ...field, options: [...(field.options || []), ''] } : field,
                          ),
                      }
                    : section,
            ),
        }));
    };

    const updateOption = (sectionIndex, fieldIndex, optionIndex, value) => {
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, sIndex) =>
                sIndex === sectionIndex
                    ? {
                          ...section,
                          fields: section.fields.map((field, fIndex) =>
                              fIndex === fieldIndex
                                  ? {
                                        ...field,
                                        options: field.options.map((opt, oIndex) =>
                                            oIndex === optionIndex ? value : opt,
                                        ),
                                    }
                                  : field,
                          ),
                      }
                    : section,
            ),
        }));
    };

    const removeOption = (sectionIndex, fieldIndex, optionIndex) => {
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, sIndex) =>
                sIndex === sectionIndex
                    ? {
                          ...section,
                          fields: section.fields.map((field, fIndex) =>
                              fIndex === fieldIndex
                                  ? {
                                        ...field,
                                        options: field.options.filter((_, oIndex) => oIndex !== optionIndex),
                                    }
                                  : field,
                          ),
                      }
                    : section,
            ),
        }));
    };

    const removeField = (sectionIndex, fieldIndex) => {
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, index) =>
                index === sectionIndex
                    ? {
                          ...section,
                          fields: section.fields.filter((_, fIndex) => fIndex !== fieldIndex),
                      }
                    : section,
            ),
        }));
    };

    const updateField = (sectionIndex, fieldIndex, fieldProp, value) => {
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, index) =>
                index === sectionIndex
                    ? {
                          ...section,
                          fields: section.fields.map((field, fIndex) =>
                              fIndex === fieldIndex ? { ...field, [fieldProp]: value } : field,
                          ),
                      }
                    : section,
            ),
        }));
    };

    const changeFieldType = (sectionIndex, fieldIndex, newType) => {
        setFormData((prev) => ({
            ...prev,
            sections: prev.sections.map((section, index) =>
                index === sectionIndex
                    ? {
                          ...section,
                          fields: section.fields.map((field, fIndex) => {
                              if (fIndex !== fieldIndex) return field;

                              // Créer le nouveau champ avec les propriétés de base
                              const newField = {
                                  id: field.id,
                                  type: newType,
                                  label: field.label,
                                  required: field.required,
                              };

                              // Ajouter les options pour dropdown/checkbox
                              if (newType === 'dropdown' || newType === 'checkbox') {
                                  newField.options = field.options || [''];
                              }

                              // Ajouter les propriétés photo
                              if (newType === 'photo') {
                                  newField.instructions = field.instructions || '';
                                  newField.minPhotos = field.minPhotos || 1;
                              }

                              // Ajouter les propriétés vidéo
                              if (newType === 'video') {
                                  newField.instructions = field.instructions || '';
                                  newField.minVideos = field.minVideos || 1;
                              }

                              return newField;
                          }),
                      }
                    : section,
            ),
        }));
    };

    // Handle section reordering
    const handleSectionDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setFormData((prev) => {
                const oldIndex = prev.sections.findIndex((s) => `section-${s.id}` === active.id);
                const newIndex = prev.sections.findIndex((s) => `section-${s.id}` === over.id);

                return {
                    ...prev,
                    sections: arrayMove(prev.sections, oldIndex, newIndex),
                };
            });
        }
    };

    // Handle field reordering within a section
    const handleFieldDragEnd = (sectionIndex) => (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setFormData((prev) => {
                const section = prev.sections[sectionIndex];
                const oldIndex = section.fields.findIndex((f) => `field-${sectionIndex}-${f.id}` === active.id);
                const newIndex = section.fields.findIndex((f) => `field-${sectionIndex}-${f.id}` === over.id);

                return {
                    ...prev,
                    sections: prev.sections.map((s, idx) =>
                        idx === sectionIndex ? { ...s, fields: arrayMove(s.fields, oldIndex, newIndex) } : s,
                    ),
                };
            });
        }
    };

    return (
        <form
            id={FORM_PROCEDURE_CREATE_ID}
            onSubmit={handleSubmit}
            className="px-6 py-5 md:p-[32px] space-y-[20px] w-full"
        >
            {/* Nom de la procédure */}
            <div>
                <label data-required className=" text-[15px] text-foreground mb-[12px] block">
                    {t('procedures:nomDeLaProcedure')}
                </label>
                <input
                    type="text"
                    placeholder={t('procedures:exInspectionDeTravaux')}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full bg-card rounded-[12px] px-[16px] h-[48px]  text-[14px] text-foreground placeholder:text-muted-foreground outline-none"
                    required
                />
            </div>

            {/* Descriptif */}
            <div>
                <label className=" text-[15px] text-foreground mb-[12px] block">{t('procedures:descriptifFacultatif')}</label>
                <textarea
                    placeholder={t('procedures:descriptionDetailleeDeLaProcedure')}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={3}
                    className="w-full bg-card rounded-[12px] px-[16px] py-[12px]  text-[14px] text-foreground placeholder:text-muted-foreground outline-none resize-none border border-border"
                />
            </div>

            {/* Couleur de la procédure */}
            <div>
                <label className=" text-[15px] text-foreground mb-[12px] block">{t('procedures:couleurDeLaProcedure')}</label>
                <Select value={formData.color} onValueChange={(value) => handleChange('color', value)}>
                    <SelectTrigger className="w-full bg-card rounded-[12px] px-[16px] h-[48px]  text-[14px] text-foreground border-border">
                        <SelectValue>
                            <div className="flex items-center gap-[12px]">
                                <div
                                    className="h-[24px] w-[24px] rounded-full border border-border"
                                    style={{ backgroundColor: formData.color }}
                                />
                                <span>{formData.color}</span>
                            </div>
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-card rounded-[12px] border border-border max-h-[300px] overflow-y-auto">
                        {COLORS.map((color) => (
                            <SelectItem
                                key={color}
                                value={color}
                                className="cursor-pointer hover:bg-muted rounded-[8px] py-[10px] px-[12px]"
                            >
                                <div className="flex items-center gap-[12px]">
                                    <div
                                        className="h-[24px] w-[24px] rounded-full border border-border"
                                        style={{ backgroundColor: color }}
                                    />
                                    <span className=" text-[14px] text-foreground">{color}</span>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Paramètres */}
            <div>
                <label className=" text-[15px] text-foreground mb-[12px] block">{t('procedures:parametres')}</label>
                <div className="bg-card rounded-[16px] p-[20px] space-y-[16px]">
                    {/* Verrouiller la procédure */}
                    <div className="flex items-center justify-between rounded-lg bg-muted px-4 py-3">
                        <div className="flex items-center gap-[12px]">
                            <div className="flex items-center justify-center w-[40px] h-[40px] rounded-[10px] border border-border bg-card">
                                <Lock className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                                <p className="text-[14px] text-foreground font-medium">{t('procedures:verrouillerLaProcedure')}</p>
                                <p className="text-[12px] text-muted-foreground">
                                    {t('procedures:empecheLaModificationDuTemplateApresCreation')}
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={formData.is_locked}
                            onCheckedChange={(checked) => handleChange('is_locked', checked)}
                        />
                    </div>

                    {/* [DISABLED] Joindre les certificats photos — pas encore développé
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-[12px]">
                            <div className="flex items-center justify-center w-[40px] h-[40px] rounded-[10px] border border-border">
                                <Camera className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                                <p className="text-[14px] text-foreground font-medium">Joindre les certificats photos</p>
                                <p className="text-[12px] text-muted-foreground">
                                    Inclure les certificats d'authenticité dans le rapport PDF
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={formData.include_photo_certificates}
                            onCheckedChange={(checked) => handleChange('include_photo_certificates', checked)}
                        />
                    </div>
                    */}

                    {/* Attribution des permissions */}
                    <label data-required className="block text-[14px] text-foreground font-medium mt-3 mb-2">
                        {t('procedures:visiblePar')}
                    </label>
                    <PermissionsSelector
                        selected_companies={formData.selected_companies}
                        onChange={(selected_companies) => handleChange('selected_companies', selected_companies)}
                    />
                </div>
            </div>

            {/* Sections et champs */}
            <div>
                <h3 className=" text-[15px] text-foreground mb-[12px]">{t('procedures:sectionsEtChamps')}</h3>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                    <SortableContext
                        items={formData.sections.map((s) => `section-${s.id}`)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-[12px] mb-[12px]">
                            {formData.sections.map((section, sectionIndex) => {
                                return (
                                    <SortableSection key={section.id} section={section}>
                                        {({ dragHandle }) => (
                                            <div className="bg-card rounded-[12px] p-[12px] md:p-[21px] border border-border">
                                                {/* Mobile: drag + delete on same row */}
                                                <div className="mobile-only md:hidden flex items-center justify-between mb-[8px]">
                                                    {dragHandle}
                                                    {formData.sections.length > 1 && (
                                                        <Button
                                                            type="button"
                                                            variant="plain"
                                                            color="destructive"
                                                            size="xsm"
                                                            onClick={() => removeSection(sectionIndex)}
                                                            className="hover:text-destructive hover:bg-destructive/15 h-[36px] px-[8px]"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className="flex md:gap-[12px]">
                                                    {/* Desktop: dragHandle on the left */}
                                                    <div className="desktop-only hidden md:block">{dragHandle}</div>
                                                    <div className="flex-1 min-w-0">
                                                        {/* Section Header */}
                                                        <div className="flex items-center gap-[8px] mb-[12px]">
                                                            <input
                                                                type="text"
                                                                placeholder={`Section ${sectionIndex + 1}`}
                                                                value={section.name}
                                                                onChange={(e) =>
                                                                    updateSection(sectionIndex, 'name', e.target.value)
                                                                }
                                                                className="flex-1 min-w-0 bg-muted rounded-[8px] px-[12px] h-[44px]  text-[14px] text-foreground placeholder:text-muted-foreground outline-none border border-border"
                                                            />
                                                            <div className="relative">
                                                                <Button
                                                                    type="button"
                                                                    variant="plain"
                                                                    color="neutral"
                                                                    size="auto"
                                                                    onClick={() =>
                                                                        setExpandedEmojiPicker(
                                                                            expandedEmojiPicker === sectionIndex
                                                                                ? null
                                                                                : sectionIndex,
                                                                        )
                                                                    }
                                                                    className="bg-muted rounded-[8px] h-[44px] w-[44px] flex items-center justify-center hover:bg-border transition-colors p-0 text-[18px]"
                                                                >
                                                                    {section.emoji || '📋'}
                                                                </Button>
                                                                {/* Emoji Picker */}
                                                                {expandedEmojiPicker === sectionIndex && (
                                                                    <div className="absolute bottom-full right-0 mb-[8px] bg-card rounded-[12px] shadow-lg p-[12px] z-40 w-[280px]">
                                                                        <div className="flex items-center justify-between mb-[8px]">
                                                                            <p className=" text-[12px] text-muted-foreground">
                                                                                {t('procedures:choisirUneIcone')}
                                                                            </p>
                                                                            <Button
                                                                                variant="plain"
                                                                                color="secondary"
                                                                                size="auto"
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    updateSection(
                                                                                        sectionIndex,
                                                                                        'emoji',
                                                                                        null,
                                                                                    );
                                                                                    setExpandedEmojiPicker(null);
                                                                                }}
                                                                                className="text-[11px] hover:text-secondary h-auto p-0"
                                                                            >
                                                                                {t('procedures:aucunEmoji')}
                                                                            </Button>
                                                                        </div>
                                                                        <div className="grid grid-cols-8 gap-[6px] max-h-[200px] overflow-y-auto overflow-x-hidden scrollbar-hide">
                                                                            {SECTION_EMOJIS.map((emoji) => (
                                                                                <Button
                                                                                    key={emoji}
                                                                                    variant="plain"
                                                                                    color="neutral"
                                                                                    size="auto"
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        updateSection(
                                                                                            sectionIndex,
                                                                                            'emoji',
                                                                                            emoji,
                                                                                        );
                                                                                        setExpandedEmojiPicker(null);
                                                                                    }}
                                                                                    className="h-[32px] w-[32px] p-0 text-[18px]"
                                                                                >
                                                                                    {emoji}
                                                                                </Button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {formData.sections.length > 1 && (
                                                                <Button
                                                                    type="button"
                                                                    variant="plain"
                                                                    color="destructive"
                                                                    size="xmd"
                                                                    onClick={() => removeSection(sectionIndex)}
                                                                    className="hidden md:flex hover:text-destructive hover:bg-destructive/15 h-[44px] px-[12px]"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>

                                                        {/* Section Description */}
                                                        <div className="mb-[12px]">
                                                            <input
                                                                type="text"
                                                                placeholder={t('procedures:descriptifDeLaSectionFacultatif')}
                                                                value={section.description}
                                                                onChange={(e) =>
                                                                    updateSection(
                                                                        sectionIndex,
                                                                        'description',
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                className="w-full bg-muted rounded-[8px] px-[12px] h-[44px]  text-[13px] text-muted-foreground placeholder:text-muted-foreground outline-none border border-border"
                                                            />
                                                        </div>

                                                        {/* Fields */}
                                                        {section.fields.length > 0 && (
                                                            <DndContext
                                                                sensors={sensors}
                                                                collisionDetection={closestCenter}
                                                                onDragEnd={handleFieldDragEnd(sectionIndex)}
                                                            >
                                                                <SortableContext
                                                                    items={section.fields.map(
                                                                        (f) => `field-${sectionIndex}-${f.id}`,
                                                                    )}
                                                                    strategy={verticalListSortingStrategy}
                                                                >
                                                                    <div className="space-y-[10px] mb-[10px]">
                                                                        {section.fields.map((field, fieldIndex) => {
                                                                            const FieldIcon = getFieldIcon(field.type);
                                                                            return (
                                                                                <SortableField
                                                                                    key={field.id}
                                                                                    field={field}
                                                                                    sectionIndex={sectionIndex}
                                                                                >
                                                                                    {({ dragHandle }) => (
                                                                                        <div className="bg-muted rounded-[10px] p-[12px]">
                                                                                            {/* Mobile: Row 1 - drag handle left, delete right */}
                                                                                            <div className="mobile-only md:hidden flex items-center justify-between mb-[8px]">
                                                                                                {dragHandle}
                                                                                                <Button
                                                                                                    type="button"
                                                                                                    variant="plain"
                                                                                                    color="destructive"
                                                                                                    size="xsm"
                                                                                                    onClick={() =>
                                                                                                        removeField(
                                                                                                            sectionIndex,
                                                                                                            fieldIndex,
                                                                                                        )
                                                                                                    }
                                                                                                    className="hover:text-destructive hover:bg-destructive/15 h-[36px] px-[8px] flex-shrink-0"
                                                                                                >
                                                                                                    <X className="h-4 w-4" />
                                                                                                </Button>
                                                                                            </div>
                                                                                            {/* Mobile: Row 2 - type + label / Desktop: all on one row */}
                                                                                            <div className="flex items-center gap-[8px] md:gap-[10px] mb-[8px]">
                                                                                                {/* Desktop only: drag handle */}
                                                                                                <div className="desktop-only hidden md:block">
                                                                                                    {dragHandle}
                                                                                                </div>
                                                                                                <DropdownMenu>
                                                                                                    <DropdownMenuTrigger
                                                                                                        asChild
                                                                                                    >
                                                                                                        <Button
                                                                                                            variant="plain"
                                                                                                            color="neutral"
                                                                                                            size="auto"
                                                                                                            type="button"
                                                                                                            className="bg-card rounded-[6px] h-[44px] w-[44px]"
                                                                                                            title={t('procedures:changerLeTypeDeChamp')}
                                                                                                        >
                                                                                                            <FieldIcon className="h-4 w-4" />
                                                                                                        </Button>
                                                                                                    </DropdownMenuTrigger>
                                                                                                    <DropdownMenuContent
                                                                                                        align="start"
                                                                                                        className="rounded-[12px] p-1 min-w-[200px]"
                                                                                                        collisionPadding={{
                                                                                                            top: 20,
                                                                                                            bottom: 20,
                                                                                                            left: 10,
                                                                                                            right: 10,
                                                                                                        }}
                                                                                                    >
                                                                                                        {FIELD_TYPES.map(
                                                                                                            (
                                                                                                                fieldType,
                                                                                                            ) => {
                                                                                                                const Icon =
                                                                                                                    fieldType.icon;
                                                                                                                const isSelected =
                                                                                                                    field.type ===
                                                                                                                    fieldType.id;
                                                                                                                return (
                                                                                                                    <DropdownMenuItem
                                                                                                                        key={
                                                                                                                            fieldType.id
                                                                                                                        }
                                                                                                                        onClick={() =>
                                                                                                                            changeFieldType(
                                                                                                                                sectionIndex,
                                                                                                                                fieldIndex,
                                                                                                                                fieldType.id,
                                                                                                                            )
                                                                                                                        }
                                                                                                                        className={`flex items-center gap-[12px] px-[16px] py-[12px] cursor-pointer rounded-[8px] ${isSelected ? 'bg-secondary/10' : ''}`}
                                                                                                                    >
                                                                                                                        <Icon
                                                                                                                            className={`w-4 h-4 ${isSelected ? 'text-secondary' : 'text-muted-foreground'}`}
                                                                                                                        />
                                                                                                                        <span
                                                                                                                            className={`text-[14px] ${isSelected ? 'text-secondary font-medium' : 'text-foreground'}`}
                                                                                                                        >
                                                                                                                            {
                                                                                                                                fieldType.label
                                                                                                                            }
                                                                                                                        </span>
                                                                                                                    </DropdownMenuItem>
                                                                                                                );
                                                                                                            },
                                                                                                        )}
                                                                                                    </DropdownMenuContent>
                                                                                                </DropdownMenu>
                                                                                                <input
                                                                                                    type="text"
                                                                                                    placeholder={
                                                                                                        field.type ===
                                                                                                        'photo'
                                                                                                            ? t('procedures:exPhotosDuChantier')
                                                                                                            : field.type ===
                                                                                                                'video'
                                                                                                              ? t('procedures:exVideosDuChantier')
                                                                                                              : t('procedures:labelDuChamp')
                                                                                                    }
                                                                                                    value={field.label}
                                                                                                    onChange={(e) =>
                                                                                                        updateField(
                                                                                                            sectionIndex,
                                                                                                            fieldIndex,
                                                                                                            'label',
                                                                                                            e.target
                                                                                                                .value,
                                                                                                        )
                                                                                                    }
                                                                                                    className="flex-1 min-w-0 bg-card rounded-[8px] px-[12px] h-[44px] text-[13px] text-foreground placeholder:text-muted-foreground outline-none border border-border"
                                                                                                />
                                                                                                {/* Desktop only: delete button */}
                                                                                                <Button
                                                                                                    type="button"
                                                                                                    variant="plain"
                                                                                                    color="destructive"
                                                                                                    size="xsm"
                                                                                                    onClick={() =>
                                                                                                        removeField(
                                                                                                            sectionIndex,
                                                                                                            fieldIndex,
                                                                                                        )
                                                                                                    }
                                                                                                    className="hidden md:flex hover:text-destructive hover:bg-destructive/15 h-[44px] px-[8px] flex-shrink-0"
                                                                                                >
                                                                                                    <X className="h-4 w-4" />
                                                                                                </Button>
                                                                                            </div>

                                                                                            {/* Options for dropdown and checkbox */}
                                                                                            {(field.type ===
                                                                                                'dropdown' ||
                                                                                                field.type ===
                                                                                                    'checkbox') && (
                                                                                                <div className="space-y-[6px] mb-[6px] md:ml-[54px]">
                                                                                                    {(
                                                                                                        field.options ||
                                                                                                        []
                                                                                                    ).map(
                                                                                                        (
                                                                                                            option,
                                                                                                            optionIndex,
                                                                                                        ) => (
                                                                                                            <div
                                                                                                                key={
                                                                                                                    optionIndex
                                                                                                                }
                                                                                                                className="flex items-center gap-[6px]"
                                                                                                            >
                                                                                                                <input
                                                                                                                    type="text"
                                                                                                                    placeholder={`Option ${optionIndex + 1}`}
                                                                                                                    value={
                                                                                                                        option
                                                                                                                    }
                                                                                                                    onChange={(
                                                                                                                        e,
                                                                                                                    ) =>
                                                                                                                        updateOption(
                                                                                                                            sectionIndex,
                                                                                                                            fieldIndex,
                                                                                                                            optionIndex,
                                                                                                                            e
                                                                                                                                .target
                                                                                                                                .value,
                                                                                                                        )
                                                                                                                    }
                                                                                                                    className="flex-1 bg-card rounded-[6px] px-[10px] h-[44px] text-[12px] text-foreground placeholder:text-muted-foreground outline-none border border-border"
                                                                                                                />
                                                                                                                {field
                                                                                                                    .options
                                                                                                                    .length >
                                                                                                                    1 && (
                                                                                                                    <Button
                                                                                                                        type="button"
                                                                                                                        variant="plain"
                                                                                                                        color="destructive"
                                                                                                                        size="xsm"
                                                                                                                        onClick={() =>
                                                                                                                            removeOption(
                                                                                                                                sectionIndex,
                                                                                                                                fieldIndex,
                                                                                                                                optionIndex,
                                                                                                                            )
                                                                                                                        }
                                                                                                                        className="hover:text-destructive hover:bg-destructive/15 h-[44px] px-[8px]"
                                                                                                                    >
                                                                                                                        <X className="h-4 w-4" />
                                                                                                                    </Button>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        ),
                                                                                                    )}
                                                                                                    <Button
                                                                                                        type="button"
                                                                                                        variant="link"
                                                                                                        color="secondary"
                                                                                                        size="auto"
                                                                                                        onClick={() =>
                                                                                                            addOption(
                                                                                                                sectionIndex,
                                                                                                                fieldIndex,
                                                                                                            )
                                                                                                        }
                                                                                                        className="text-[11px] p-0 h-auto"
                                                                                                    >
                                                                                                        {t('procedures:ajouterUneOption')}
                                                                                                    </Button>
                                                                                                </div>
                                                                                            )}

                                                                                            {/* Instructions for photo/video field */}
                                                                                            {(field.type === 'photo' ||
                                                                                                field.type === 'video') && (
                                                                                                <>
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        placeholder={t('procedures:consignesDePrisesDeVueFacultatif')}
                                                                                                        value={
                                                                                                            field.instructions ||
                                                                                                            ''
                                                                                                        }
                                                                                                        onChange={(e) =>
                                                                                                            updateField(
                                                                                                                sectionIndex,
                                                                                                                fieldIndex,
                                                                                                                'instructions',
                                                                                                                e.target
                                                                                                                    .value,
                                                                                                            )
                                                                                                        }
                                                                                                        className="w-full md:w-[calc(100%-54px)] bg-card rounded-[8px] px-[12px] h-[44px] text-[12px] text-foreground placeholder:text-muted-foreground outline-none md:ml-[54px] mb-[6px] border border-border"
                                                                                                    />
                                                                                                    <div className="flex flex-wrap items-center gap-[8px] md:ml-[54px] mb-[6px]">
                                                                                                        <label className="text-[11px] text-muted-foreground whitespace-nowrap">
                                                                                                            {t('procedures:nombreMinimumDe')}{' '}
                                                                                                            {field.type ===
                                                                                                            'video'
                                                                                                                ? t('procedures:videos')
                                                                                                                : 'photos'}{' '}
                                                                                                            :
                                                                                                        </label>
                                                                                                        <input
                                                                                                            type="number"
                                                                                                            min="1"
                                                                                                            max="20"
                                                                                                            value={
                                                                                                                field.type ===
                                                                                                                'video'
                                                                                                                    ? field.minVideos ||
                                                                                                                      1
                                                                                                                    : field.minPhotos ||
                                                                                                                      1
                                                                                                            }
                                                                                                            onChange={(
                                                                                                                e,
                                                                                                            ) =>
                                                                                                                updateField(
                                                                                                                    sectionIndex,
                                                                                                                    fieldIndex,
                                                                                                                    field.type ===
                                                                                                                        'video'
                                                                                                                        ? 'minVideos'
                                                                                                                        : 'minPhotos',
                                                                                                                    Math.max(
                                                                                                                        1,
                                                                                                                        parseInt(
                                                                                                                            e
                                                                                                                                .target
                                                                                                                                .value,
                                                                                                                        ) ||
                                                                                                                            1,
                                                                                                                    ),
                                                                                                                )
                                                                                                            }
                                                                                                            className="w-[60px] bg-card rounded-[8px] px-[8px] h-[32px] text-[12px] text-foreground outline-none border border-border text-center"
                                                                                                        />
                                                                                                    </div>
                                                                                                </>
                                                                                            )}

                                                                                            {/* Required checkbox */}
                                                                                            <label className="flex items-center gap-[6px] cursor-pointer md:ml-[54px]">
                                                                                                <Checkbox
                                                                                                    checked={
                                                                                                        field.required
                                                                                                    }
                                                                                                    onCheckedChange={(
                                                                                                        checked,
                                                                                                    ) =>
                                                                                                        updateField(
                                                                                                            sectionIndex,
                                                                                                            fieldIndex,
                                                                                                            'required',
                                                                                                            checked,
                                                                                                        )
                                                                                                    }
                                                                                                    className="w-[14px] h-[14px]"
                                                                                                />
                                                                                                <span className=" text-[11px] text-muted-foreground">
                                                                                                    {t('procedures:champObligatoire')}
                                                                                                </span>
                                                                                            </label>
                                                                                        </div>
                                                                                    )}
                                                                                </SortableField>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </SortableContext>
                                                            </DndContext>
                                                        )}

                                                        {/* Add Field Button */}
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    color="neutral"
                                                                    className="bg-muted rounded-[10px] w-full py-[10px] hover:bg-border transition-colors flex items-center justify-center gap-[6px] border border-dashed border-foreground h-auto"
                                                                >
                                                                    <Plus
                                                                        className="h-4 w-4 text-foreground"
                                                                        strokeWidth={2.5}
                                                                    />
                                                                    <span className="text-[13px] text-foreground">
                                                                        {t('procedures:ajouterUnChamp')}
                                                                    </span>
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent
                                                                align="start"
                                                                className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-[12px] p-1"
                                                                collisionPadding={{
                                                                    top: 20,
                                                                    bottom: 20,
                                                                    left: 10,
                                                                    right: 10,
                                                                }}
                                                            >
                                                                {FIELD_TYPES.map((fieldType) => {
                                                                    const Icon = fieldType.icon;
                                                                    return (
                                                                        <DropdownMenuItem
                                                                            key={fieldType.id}
                                                                            onClick={() =>
                                                                                addField(sectionIndex, fieldType.id)
                                                                            }
                                                                            className="flex items-center gap-[12px] px-[16px] py-[12px] cursor-pointer rounded-[8px]"
                                                                        >
                                                                            <Icon className="w-4 h-4 text-muted-foreground" />
                                                                            <span className="text-[14px] text-foreground">
                                                                                {fieldType.label}
                                                                            </span>
                                                                        </DropdownMenuItem>
                                                                    );
                                                                })}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </SortableSection>
                                );
                            })}
                        </div>
                    </SortableContext>
                </DndContext>

                {/* Add Section Button */}
                <Button
                    variant="solid"
                    color="neutral"
                    size="xxl"
                    type="button"
                    onClick={addSection}
                    className="rounded-[12px] w-full py-[12px] flex items-center justify-center gap-[6px] h-auto"
                >
                    <Plus className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
                    <span className=" text-[14px] text-primary-foreground">{t('procedures:ajouterUneSection')}</span>
                </Button>
            </div>
        </form>
    );
}
